// Narration tab of the F-F media panel. One row per narration language
// (TC=Cantonese, SC=Mandarin, EN; per ADR-0024 D4). Each row shows the
// current source, an audio preview when stored, and the actions:
// Generate/Regenerate (MiniMax, synchronous in v1), Generate custom audio
// (F-O: an inline editor that synthesizes from freely edited text — see
// below), Download (fetch the stored audio to edit externally), Upload
// custom (replaces), and Remove (clears the ref → falls back to the
// legacy voice_url when present, else no narration).
//
// Presentational + controlled: the container owns the mutations. Source is
// derived from the entity response (narration_asset_id_* / voice_url_* /
// narration_source_*): stored audio badges as "AI-generated" or "Custom
// audio" via the slot-level provenance flag, falling back to the generic
// "Stored audio" for legacy rows that predate the flag.
//
// #2: when the description for a language is edited while narration audio is
// already in place, the row shows a live, dismissible nudge (client-side only —
// `descriptionChanged` is computed by the form from current vs saved text).
//
// F-O custom-narration editor (per-language state machine): closed →
// editing (textarea pre-filled from the saved description; never
// persisted, D2) → previewing (draft player; confirm disabled the moment
// the text no longer matches what the preview was generated from, D5).
// Confirm attaches via the ordinary set-narration path (provenance
// "custom", D4); Cancel/Discard/Regenerate first discard the old draft.

import { useState } from "react";
import { Download, Loader2, Sparkles, Trash2, Upload } from "lucide-react";

import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import type { NarrationSource } from "@shared/api/exhibits";
import type { AudioLanguage } from "@shared/hooks/useTextToSpeech";

import FileButton from "./FileButton";

export interface NarrationRow {
  assetId: string | null;
  voiceUrl: string;
  // Provenance of the stored asset (AI-generated vs custom upload); null /
  // absent on rows that predate the flag → generic "Stored audio" badge.
  source?: NarrationSource | null;
  // #2: the description for this language was edited after narration audio was
  // put in place — a live nudge to re-check the audio. Dismissible per language.
  descriptionChanged?: boolean;
}

export interface NarrationTabProps {
  ownerKey: string;
  assetUrl: (assetId: string) => string;
  narration: Record<AudioLanguage, NarrationRow>;
  generatingLang: AudioLanguage | null;
  disabled?: boolean;
  onGenerate: (lang: AudioLanguage) => void;
  onUploadCustom: (lang: AudioLanguage, file: File) => void;
  // Download the stored narration audio (to edit externally, then re-upload).
  onDownload: (lang: AudioLanguage) => void;
  onRemove: (lang: AudioLanguage) => void;
  // Edit/save a legacy voice URL for a language (clears it when saved empty).
  onSaveVoiceUrl: (lang: AudioLanguage, url: string) => void;
  // ── F-O custom-narration editor ──────────────────────────────────────
  // Saved description per language — the editor's pre-fill fallback.
  descriptions: Partial<Record<AudioLanguage, string>>;
  // Last confirmed custom narration text per language (D2 reversal,
  // 2026-09-02) — preferred pre-fill so staff resume refining their last
  // version instead of starting over from the description.
  customNarrationTexts?: Partial<Record<AudioLanguage, string>>;
  // Monthly AI budget exhausted: disables every generate path (standard +
  // custom) with a note. Upload custom stays enabled (D8).
  generationBlocked?: boolean;
  // Synthesize a draft from `text`; resolves to the draft assetId, or null
  // when generation failed (the container has already toasted).
  onPreview: (lang: AudioLanguage, text: string) => Promise<string | null>;
  // Attach a draft as the language's narration (source=custom); `text` is
  // what the draft was synthesized from — persisted server-side as the
  // editor's next pre-fill. Resolves false on failure (container toasted);
  // the editor stays open.
  onConfirmPreview: (
    lang: AudioLanguage,
    assetId: string,
    text: string,
  ) => Promise<boolean>;
  // Delete an unwanted draft (fire-and-forget from the editor's viewpoint).
  onDiscardPreview: (assetId: string) => Promise<void>;
}

// F-O editor state for one language row; absence in the map = closed.
interface DraftState {
  text: string;
  previewAssetId: string | null;
  // The exact text the current preview was generated from (D5).
  previewText: string | null;
  busy: boolean;
}

const LANGS: { lang: AudioLanguage; heading: string }[] = [
  { lang: "tc", heading: "繁體中文 (Cantonese)" },
  { lang: "en", heading: "English" },
  { lang: "sc", heading: "简体中文 (Mandarin)" },
];

function sourceBadge(row: NarrationRow): { label: string; className: string } {
  if (row.assetId) {
    if (row.source === "ai") {
      return {
        label: "AI-generated",
        className: "bg-violet-100 text-violet-800",
      };
    }
    if (row.source === "custom") {
      return { label: "Custom audio", className: "bg-blue-100 text-blue-800" };
    }
    // Legacy rows stored before the provenance flag existed.
    return {
      label: "Stored audio",
      className: "bg-green-100 text-green-800",
    };
  }
  if (row.voiceUrl) {
    return { label: "Legacy URL", className: "bg-amber-100 text-amber-800" };
  }
  // Nothing stored and no legacy URL — the visitor gets no narration
  // (the browser-TTS fallback was retired, 2026-08-11 review #2).
  return { label: "No narration", className: "bg-gray-100 text-gray-700" };
}

const NarrationTab = ({
  ownerKey,
  assetUrl,
  narration,
  generatingLang,
  disabled,
  onGenerate,
  onUploadCustom,
  onDownload,
  onRemove,
  onSaveVoiceUrl,
  descriptions,
  customNarrationTexts,
  generationBlocked,
  onPreview,
  onConfirmPreview,
  onDiscardPreview,
}: NarrationTabProps) => {
  // Per-language local dismissal of the "description changed" nudge (#2). Live
  // and client-side only — resets when the form is reopened.
  const [dismissed, setDismissed] = useState<
    Partial<Record<AudioLanguage, boolean>>
  >({});
  // Per-language draft of the legacy voice URL being edited (committed on Save).
  const [urlDrafts, setUrlDrafts] = useState<
    Partial<Record<AudioLanguage, string>>
  >({});
  // F-O custom-narration editor state; a language absent here is closed.
  const [customDrafts, setCustomDrafts] = useState<
    Partial<Record<AudioLanguage, DraftState>>
  >({});

  const patchDraft = (lang: AudioLanguage, patch: Partial<DraftState>) =>
    setCustomDrafts((d) => {
      const cur = d[lang];
      return cur ? { ...d, [lang]: { ...cur, ...patch } } : d;
    });

  const closeEditor = (lang: AudioLanguage) =>
    setCustomDrafts((d) => {
      const next = { ...d };
      delete next[lang];
      return next;
    });

  const openEditor = (lang: AudioLanguage) =>
    setCustomDrafts((d) => ({
      ...d,
      [lang]: {
        // Resume from the last confirmed custom text when there is one;
        // fall back to the saved description.
        text: customNarrationTexts?.[lang] || descriptions[lang] || "",
        previewAssetId: null,
        previewText: null,
        busy: false,
      },
    }));

  // Cancel (pre-preview) and Discard (post-preview): drop the draft asset
  // if one exists, then close. The discard is best-effort — a failure has
  // already been toasted by the container and only strands one invisible
  // draft (accepted, F-O orphan note).
  const cancelEditor = async (lang: AudioLanguage) => {
    const draft = customDrafts[lang];
    closeEditor(lang);
    if (draft?.previewAssetId) await onDiscardPreview(draft.previewAssetId);
  };

  // Generate preview (also serves Regenerate: the old draft is discarded
  // first so abandoned takes never accumulate).
  const generatePreview = async (lang: AudioLanguage) => {
    const draft = customDrafts[lang];
    if (!draft || !draft.text.trim()) return;
    patchDraft(lang, { busy: true });
    if (draft.previewAssetId) await onDiscardPreview(draft.previewAssetId);
    const assetId = await onPreview(lang, draft.text);
    patchDraft(
      lang,
      assetId
        ? { busy: false, previewAssetId: assetId, previewText: draft.text }
        : { busy: false, previewAssetId: null, previewText: null },
    );
  };

  const confirmPreview = async (lang: AudioLanguage) => {
    const draft = customDrafts[lang];
    if (!draft?.previewAssetId) return;
    patchDraft(lang, { busy: true });
    // previewText is what the draft audio was actually synthesized from
    // (confirm is disabled whenever draft.text has drifted from it, D5).
    const ok = await onConfirmPreview(
      lang,
      draft.previewAssetId,
      draft.previewText ?? draft.text,
    );
    if (ok) {
      closeEditor(lang); // the asset is attached now — nothing to discard
    } else {
      patchDraft(lang, { busy: false });
    }
  };

  return (
    <div className="space-y-4" data-testid="narration-tab">
      {LANGS.map(({ lang, heading }) => {
        const row = narration[lang];
        const badge = sourceBadge(row);
        const generating = generatingLang === lang;
        const busy = disabled || generating;
        // F-O custom-narration editor state for this row (undefined = closed).
        const draft = customDrafts[lang];
        const previewStale =
          !!draft?.previewAssetId && draft.text !== draft.previewText;
        // Narration is "in place" via a stored asset OR a legacy voice URL —
        // both were made for the old text and can go stale. Browser-TTS (neither)
        // reads the live description, so it's never stale.
        const hasNarration = !!row.assetId || !!row.voiceUrl;
        const showChangedNudge =
          hasNarration && !!row.descriptionChanged && !dismissed[lang];
        return (
          <div
            key={lang}
            className="space-y-3 p-4 bg-white border border-border rounded-lg"
            data-testid={`narration-row-${lang}`}
          >
            <div className="flex items-center justify-between">
              <h4 className="font-heading font-semibold">{heading}</h4>
              <span
                className={`text-xs px-2 py-1 rounded ${badge.className}`}
                data-testid={`narration-source-${lang}`}
              >
                {badge.label}
              </span>
            </div>

            {!row.assetId && (
              <div className="flex items-center gap-2">
                <Input
                  value={urlDrafts[lang] ?? row.voiceUrl}
                  onChange={(e) =>
                    setUrlDrafts((d) => ({ ...d, [lang]: e.target.value }))
                  }
                  disabled={busy}
                  placeholder="Audio URL (optional)"
                  className="font-mono text-xs"
                  data-testid={`narration-url-${lang}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={
                    busy || (urlDrafts[lang] ?? row.voiceUrl) === row.voiceUrl
                  }
                  onClick={() =>
                    onSaveVoiceUrl(lang, urlDrafts[lang] ?? row.voiceUrl)
                  }
                  data-testid={`narration-url-save-${lang}`}
                >
                  Save URL
                </Button>
              </div>
            )}

            {showChangedNudge && (
              <div
                className="flex items-start justify-between gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
                data-testid={`narration-stale-${lang}`}
              >
                <span>
                  ⚠ The description changed after this narration audio was put
                  in place — you may want to check it&apos;s up to date.
                </span>
                <button
                  type="button"
                  onClick={() => setDismissed((d) => ({ ...d, [lang]: true }))}
                  className="shrink-0 underline text-amber-700"
                  data-testid={`narration-stale-dismiss-${lang}`}
                >
                  Dismiss
                </button>
              </div>
            )}

            {(() => {
              // Player for whatever is currently playable: the stored narration
              // asset, else the legacy voice_url. None → browser TTS (no file to
              // preview here).
              const previewSrc = row.assetId
                ? assetUrl(row.assetId)
                : row.voiceUrl || null;
              return previewSrc ? (
                <audio
                  controls
                  preload="none"
                  src={previewSrc}
                  className="w-full"
                  data-testid={`narration-preview-${lang}`}
                />
              ) : null;
            })()}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy || generationBlocked}
                onClick={() => onGenerate(lang)}
                data-testid={`narration-generate-${lang}`}
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-1" />
                )}
                {generating
                  ? "Generating…"
                  : row.assetId
                    ? "Regenerate"
                    : "Generate"}
              </Button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy || generationBlocked || !!draft}
                onClick={() => openEditor(lang)}
                data-testid={`narration-custom-open-${lang}`}
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Generate custom audio
              </Button>

              {row.assetId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => onDownload(lang)}
                  data-testid={`narration-download-${lang}`}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
              )}

              <FileButton
                accept="audio/mpeg,audio/mp4,audio/x-wav,audio/wav"
                onFile={(file) => onUploadCustom(lang, file)}
                disabled={busy}
                testId={`narration-upload-${lang}`}
              >
                <Upload className="w-4 h-4 mr-1" />
                Upload custom
              </FileButton>

              {hasNarration && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => onRemove(lang)}
                  data-testid={`narration-remove-${lang}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove
                </Button>
              )}
            </div>

            {generationBlocked && (
              <p
                className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5"
                data-testid={`narration-blocked-note-${lang}`}
              >
                Monthly AI narration budget is used up — ask the development
                team to add a top-up grant. Uploading your own recording still
                works.
              </p>
            )}

            {draft && (
              <div
                className="space-y-2 p-3 bg-muted/40 border border-border rounded-lg"
                data-testid={`narration-custom-editor-${lang}`}
              >
                <p className="text-sm font-medium">Custom narration text</p>
                <textarea
                  value={draft.text}
                  onChange={(e) => patchDraft(lang, { text: e.target.value })}
                  disabled={draft.busy}
                  rows={4}
                  className="w-full border border-border rounded px-3 py-2 text-sm disabled:bg-muted"
                  data-testid={`narration-custom-text-${lang}`}
                />
                <p
                  className="text-xs text-muted-foreground"
                  data-testid={`narration-custom-count-${lang}`}
                >
                  {draft.text.length} characters
                </p>

                {draft.previewAssetId && (
                  <audio
                    controls
                    preload="none"
                    src={assetUrl(draft.previewAssetId)}
                    className="w-full"
                    data-testid={`narration-custom-preview-${lang}`}
                  />
                )}
                {previewStale && (
                  <p className="text-xs text-amber-700">
                    The text changed since this preview — regenerate to confirm.
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {draft.previewAssetId ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        disabled={draft.busy || previewStale}
                        onClick={() => confirmPreview(lang)}
                        data-testid={`narration-custom-confirm-${lang}`}
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Upload as custom audio
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={
                          draft.busy || generationBlocked || !draft.text.trim()
                        }
                        onClick={() => generatePreview(lang)}
                        data-testid={`narration-custom-regen-${lang}`}
                      >
                        {draft.busy ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-1" />
                        )}
                        Regenerate preview
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={draft.busy}
                        onClick={() => cancelEditor(lang)}
                        data-testid={`narration-custom-discard-${lang}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Discard
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        disabled={
                          draft.busy || generationBlocked || !draft.text.trim()
                        }
                        onClick={() => generatePreview(lang)}
                        data-testid={`narration-custom-generate-${lang}`}
                      >
                        {draft.busy ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-1" />
                        )}
                        Generate preview
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={draft.busy}
                        onClick={() => cancelEditor(lang)}
                        data-testid={`narration-custom-cancel-${lang}`}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
      <p
        className="text-xs text-muted-foreground"
        data-testid={`narration-help-${ownerKey}`}
      >
        Generate uses the configured voice for each language. To edit a
        narration, Download it, edit the file externally, then Upload custom to
        replace it. Removing stored audio falls back to the legacy voice URL if
        one exists; otherwise the item has no narration.
      </p>
    </div>
  );
};

export default NarrationTab;
